import { useState } from 'react'
import { PiCheckCircleDuotone } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Dialog from '@/components/ui/Dialog'
import Spinner from '@/components/ui/Spinner'
import { useBillingStore } from '@/store/billingStore'
import { usePlanActivationStore } from '@/store/planActivationStore'
import PlanPicker from './PlanPicker'
import usePlanCatalogue from '../hooks/usePlanCatalogue'
import { useTranslation } from 'react-i18next'
import type { ChoosePlanOutcome } from '../utils/choosePlan'

/**
 * Change plan — and pay — without leaving the page you are on.
 *
 * Opened from the drawer's plan item (`PlanNavItem`). Mounted once in
 * `Layouts` and driven by `billingStore.planDialogOpen`, because the mobile
 * drawer unmounts its contents as it closes and would take the dialog with it.
 *
 * The cards are the billing page's own `PlanPicker`, so a plan cannot read or
 * check out differently here. Under Paddle, choosing a paid plan opens Paddle's
 * overlay on top of this dialog; paying turns the dialog into "Activating…"
 * until the webhook lands (`PlanActivationWatcher`), then "You're on Pro".
 * Closing the checkout without paying leaves the cards where they were.
 *
 * Usage, payment method, add-ons and invoices stay on the billing page — this
 * is the plan choice only, with a link through for the rest.
 */
const PlanDialog = () => {
    const open = useBillingStore((state) => state.planDialogOpen)
    const close = useBillingStore((state) => state.closePlanDialog)

    return (
        <Dialog
            isOpen={open}
            width={900}
            onClose={close}
            onRequestClose={close}
        >
            {/* Only fetched while open: the summary is admin-only and not
                cheap, and this is mounted on every signed-in page. */}
            {open && <PlanDialogBody onClose={close} />}
        </Dialog>
    )
}

const PlanDialogBody = ({ onClose }: { onClose: () => void }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const catalogue = usePlanCatalogue()
    const pending = usePlanActivationStore((state) => state.pending)
    const stalled = usePlanActivationStore((state) => state.stalled)
    // The plan this dialog switched to, once it has.
    const [chosen, setChosen] = useState<string | null>(null)

    const onChosen = (planCode: string, outcome: ChoosePlanOutcome) => {
        if (outcome !== 'closed') setChosen(planCode)
    }

    const nameOf = (code: string | null) =>
        catalogue.plans.find((plan) => plan.code === code)?.name ?? code ?? ''
    const currentPlan = catalogue.plans.find(
        (plan) => plan.code === catalogue.currentPlanCode,
    )

    const toBilling = () => {
        onClose()
        navigate('/settings/billing')
    }

    if (chosen) {
        return (
            <Outcome
                chosenName={nameOf(chosen)}
                state={
                    pending === chosen
                        ? 'activating'
                        : stalled === chosen
                          ? 'stalled'
                          : 'done'
                }
                onClose={onClose}
            />
        )
    }

    return (
        <div>
            <div className="mb-6 pe-8">
                <h4 className="mb-1">{t('billing.planDialog.title')}</h4>
                <p className="text-gray-500 dark:text-gray-400">
                    {currentPlan
                        ? t('billing.planDialog.current', {
                              plan: currentPlan.name,
                          })
                        : t('billing.planDialog.body')}
                </p>
            </div>

            {pending && (
                <Alert showIcon className="mb-4" type="info">
                    <span className="inline-flex items-center gap-2">
                        <Spinner size={16} />
                        {t('billing.activating', { plan: nameOf(pending) })}
                    </span>
                </Alert>
            )}

            {catalogue.isLoading ? (
                <div className="flex justify-center py-16">
                    <Spinner size={40} />
                </div>
            ) : catalogue.unavailable ? (
                <Alert showIcon type="danger">
                    {t('billing.loadFailed')}
                </Alert>
            ) : catalogue.plans.length === 0 ? (
                <p className="py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('billing.planDialog.none')}
                </p>
            ) : (
                <PlanPicker
                    bare
                    plans={catalogue.plans}
                    currentPlanCode={catalogue.currentPlanCode}
                    hasCard={catalogue.hasCard}
                    provider={catalogue.provider}
                    onChosen={onChosen}
                />
            )}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('billing.planDialog.more')}
                </span>
                <Button size="sm" variant="plain" onClick={toBilling}>
                    {t('billing.viewBilling')}
                </Button>
            </div>
        </div>
    )
}

type OutcomeProps = {
    chosenName: string
    state: 'activating' | 'stalled' | 'done'
    onClose: () => void
}

/** After a choice: waiting for Paddle's webhook, or on the new plan. */
const Outcome = ({ chosenName, state, onClose }: OutcomeProps) => {
    const { t } = useTranslation()

    return (
        <div className="flex flex-col items-center px-4 py-10 text-center">
            {state === 'activating' ? (
                <Spinner size={48} />
            ) : (
                <PiCheckCircleDuotone className="text-6xl text-emerald-500" />
            )}
            <h4 className="mt-5">
                {state === 'done'
                    ? t('billing.planDialog.done', { plan: chosenName })
                    : t('billing.planDialog.paid')}
            </h4>
            <p className="mt-2 max-w-md text-gray-500 dark:text-gray-400">
                {state === 'activating'
                    ? t('billing.activating', { plan: chosenName })
                    : state === 'stalled'
                      ? t('billing.activationSlow')
                      : t('billing.planDialog.doneBody')}
            </p>
            {state !== 'activating' && (
                <Button className="mt-6" variant="solid" onClick={onClose}>
                    {t('common.close')}
                </Button>
            )}
        </div>
    )
}

export default PlanDialog
